/**
 * HA Signup Attribution — single source of truth.
 *
 * captureAttribution() runs on every page load (mounted via AttributionTracker
 * in App.tsx). It records first-touch UTM params, referrer, landing route, and
 * `?ref`/`?aff` markers to a single localStorage blob. First-touch wins; the
 * blob is never overwritten once present.
 *
 * getAttributionPayload() builds the flat object that every signup path must
 * spread into supabase.auth.signUp({ options: { data: ... } }). The DB trigger
 * handle_new_user maps these keys onto public.profiles columns.
 */

import { readAffiliateParams } from "./affiliateTracking";

const STORAGE_KEY = "ha_attribution";

export interface AttributionBlob {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  ref?: string;
  aff?: string;
  fbclid?: string;
  referrer?: string;
  landing_route?: string;
  captured_at: string;
}

function safeGet(): AttributionBlob | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AttributionBlob;
  } catch {
    return null;
  }
}

/** First-touch capture. No-op if a blob already exists. */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  if (safeGet()) return;

  try {
    const p = new URLSearchParams(window.location.search);
    const referrer = document.referrer || "";
    let referrerHost = "";
    try {
      if (referrer) referrerHost = new URL(referrer).host;
    } catch {}
    // Don't count same-origin referrers
    if (referrerHost && referrerHost === window.location.host) referrerHost = "";

    const blob: AttributionBlob = {
      utm_source: p.get("utm_source") || undefined,
      utm_medium: p.get("utm_medium") || undefined,
      utm_campaign: p.get("utm_campaign") || undefined,
      utm_term: p.get("utm_term") || undefined,
      utm_content: p.get("utm_content") || undefined,
      ref: p.get("ref") || undefined,
      aff: p.get("aff") || undefined,
      fbclid: p.get("fbclid") || undefined,
      referrer: referrerHost || undefined,
      landing_route: window.location.pathname || undefined,
      captured_at: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
  } catch {
    /* localStorage unavailable */
  }
}

/**
 * Build the flat metadata payload to merge into auth.signUp options.data.
 * Safe to call from any signup path; gracefully returns empty values when
 * localStorage is unavailable.
 */
export function getAttributionPayload(): Record<string, string | null> {
  const blob = safeGet();
  let lifemartSource: string | null = null;
  let lifemartRef: string | null = null;
  let lifemartPromo: string | null = null;
  let lifemartLanding: string | null = null;
  try {
    lifemartSource = localStorage.getItem("lifemart_signup_source");
    lifemartRef = localStorage.getItem("lifemart_ref");
    lifemartPromo = localStorage.getItem("lifemart_promo_code");
    lifemartLanding = localStorage.getItem("lifemart_landing_route");
  } catch {}

  const aff = readAffiliateParams();

  // Normalize obviously-unfilled affiliate macros (e.g. partners that ship a
  // template URL like `?source=subAffID` without substituting the value).
  const isMacro = (v: string | null | undefined): boolean => {
    if (!v) return false;
    return /^(subaffid|sub_?id\d?|click_?id|source|aff_?sub|\{.*\}|%7b.*%7d)$/i.test(v.trim());
  };

  // LifeMart-specific normalization: any inbound with sub_id1=LM or a
  // referrer on lm.lifemart.com is canonically `lifemart`, regardless of
  // whether the partner filled the `source` macro.
  const looksLifeMart =
    (aff?.sub_id1 || "").toUpperCase() === "LM" ||
    /(^|\.)lifemart\.com$/i.test(blob?.referrer || "");

  // Resolve the canonical signup_source with priority:
  // explicit lifemart > LifeMart pattern > UTM source > ?ref > affiliate source (if real) > referrer host > "direct"
  const signupSource =
    lifemartSource ||
    (looksLifeMart ? "lifemart" : null) ||
    (isMacro(blob?.utm_source) ? null : blob?.utm_source) ||
    (isMacro(blob?.ref) ? null : blob?.ref) ||
    (isMacro(aff?.source) ? null : aff?.source) ||
    blob?.referrer ||
    "direct";

  const landingRoute =
    lifemartLanding ||
    blob?.landing_route ||
    (typeof window !== "undefined" ? window.location.pathname : null);

  return {
    // Keep legacy keys so the existing trigger continues to work even before
    // the migration ships.
    lifemart_signup_source: lifemartSource,
    lifemart_ref: lifemartRef,
    lifemart_promo: lifemartPromo,
    signup_landing_route: landingRoute ?? null,

    // Canonical flat keys consumed by the updated handle_new_user trigger.
    signup_source: signupSource,
    signup_ref: lifemartRef || blob?.ref || null,
    signup_promo: lifemartPromo || null,
    signup_medium: blob?.utm_medium ?? null,
    signup_campaign: blob?.utm_campaign ?? null,
    signup_term: blob?.utm_term ?? null,
    signup_content: blob?.utm_content ?? null,
    signup_referrer: blob?.referrer ?? null,
    signup_fbclid: blob?.fbclid ?? null,
    aff_id: aff?.click_id ?? blob?.aff ?? null,
    aff_sub: isMacro(aff?.sub_id1) ? null : (aff?.sub_id1 ?? null),
  };
}